import { useEffect } from 'react';

import { useAsyncStorage } from '@sg/garnish';
import { setRadarMarketingPushSubscribed } from '@sg/radar-fe';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useNotificationPreferencesState } from '../useNotificationPreferencesState';

/**
 * Adds/removes the marketing push subscribed tag on the Radar SDK based on the user's marketing push notification preferences.
 * Only runs this once per installation, as the {RADAR_SYNC_KEY} local storage key is set to {HAS_SYNCED} afterwards.
 */
export const useSyncRadarMarketingPushTags = () => {
  const isLoggedIn = useIsLoggedIn();
  const isNotificationPreferencesEnabled = useFeatureFlag('CELS-3674-enable-notification-preference-center'); // prettier-ignore

  const [syncStatus, setSyncStatus] = useAsyncStorage(RADAR_SYNC_KEY);
  const isLoadingSyncState = syncStatus === undefined;
  const hasSyncedTags = syncStatus === HAS_SYNCED;

  const { isMarketingPushEnabled, loading: isLoadingNotificationPreferences } =
    useNotificationPreferencesState({
      requestPolicy: 'network-only',
      pause: isLoadingSyncState || hasSyncedTags,
    });

  const shouldSyncTags =
    isLoggedIn &&
    isNotificationPreferencesEnabled &&
    !isLoadingNotificationPreferences &&
    !isLoadingSyncState &&
    !hasSyncedTags;

  useEffect(() => {
    if (!shouldSyncTags) return;

    setRadarMarketingPushSubscribed(isMarketingPushEnabled);
    setSyncStatus(HAS_SYNCED);
  }, [isMarketingPushEnabled, shouldSyncTags, setSyncStatus]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const RADAR_SYNC_KEY = 'radar_marketing_push_tag_sync_status';
const HAS_SYNCED = 'synced';
